/**
 * Returns the shared Supabase client created by the parent index.html.
 *
 * The parent app already initializes a Supabase client and exposes it as
 * `window.sb`. We reuse it so the auth session is shared — the user is
 * already logged in by the time they switch to the Investment tab.
 *
 * If you're running the dev server standalone (no parent app),
 * window.sb won't exist and this returns null. The App component handles
 * that case with a "Supabase client not available" message.
 */
export function getSupabase() {
  if (typeof window === 'undefined') return null;
  return window.sb || null;
}

/**
 * Convenience: get the current member row for the logged-in user.
 * Returns null if no user, no profile, no member_id, or fetch fails.
 */
export async function getCurrentMember() {
  const sb = getSupabase();
  if (!sb) return null;

  const { data: { user } } = await sb.auth.getUser();
  if (!user) return null;

  const { data: profile } = await sb
    .from('profiles')
    .select('member_id')
    .eq('id', user.id)
    .single();
  if (!profile?.member_id) return null;

  const { data: member } = await sb
    .from('members')
    .select('id, name, display_name, active, managed_by_member_id')
    .eq('id', profile.member_id)
    .single();
  return member || null;
}

/**
 * Get the IDs of all members the current user can act on:
 * themselves + any sub-accounts they manage.
 */
export async function getMyManagedMemberIds() {
  const sb = getSupabase();
  if (!sb) return [];

  const me = await getCurrentMember();
  if (!me) return [];

  const { data: managed } = await sb
    .from('members')
    .select('id')
    .eq('managed_by_member_id', me.id);

  return [me.id, ...(managed || []).map((m) => m.id)];
}
